import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui";

import { type RowAction } from "src/components/DataTable";

interface DataTableActionDialogProps {
  action: RowAction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DataTableActionDialog(props: DataTableActionDialogProps) {
  const { t } = useTranslation();
  const dialog = props.action.dialog!;

  const mutation = useMutation({
    mutationFn: props.action.mutationFn ?? (() => Promise.resolve()),
    onSuccess: (data) => {
      props.action.onSuccess?.(data);
      props.onOpenChange(false);
    },
    onError: (error) => {
      props.action.onError?.(error);
    },
    onSettled: (data, error) => {
      props.action.onSettled?.(data, error);
    },
  });

  function handleSubmit() {
    if (props.action.mutationFn) {
      mutation.mutate();
    } else {
      props.action.onSelect?.();
      props.onOpenChange(false);
    }
  }

  if (dialog.content) {
    return <>{dialog.content}</>;
  }

  const title = dialog.title ?? props.action.label;
  const description =
    dialog.description ??
    (dialog.body
      ? null
      : t("dataTable.confirmAction", {
          action: (props.action.label ?? "").toLowerCase(),
        }));

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          {title && <DialogTitle>{title}</DialogTitle>}
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {dialog.body && <div>{dialog.body}</div>}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              return props.onOpenChange(false);
            }}
          >
            {t("dataTable.cancel")}
          </Button>
          <Button
            variant={dialog.destructive ? "destructive" : "default"}
            onClick={handleSubmit}
            loading={mutation.isPending}
          >
            {props.action.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
